import React from 'react';
import {
  hasGoogleMapsTarget,
  joinLocationQuery,
  resolveGoogleMapsHref,
} from '../../utils/googleMaps';

const MapPinIcon = ({ className = 'w-12 h-12' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

const ExternalLinkIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
    />
  </svg>
);

/**
 * Opens Google Maps (app when installed, otherwise maps.google.com).
 * variant "card" — tappable preview block; "link" — inline text link.
 */
const GoogleMapsLocationLink = ({
  latitude,
  longitude,
  query,
  address,
  city,
  venue,
  area,
  name,
  society,
  label = 'Open in Google Maps',
  variant = 'link',
  heading = null,
  description,
  className = '',
  children,
}) => {
  const locationQuery =
    query || joinLocationQuery([address, area, city, venue, name, society]);
  const href = resolveGoogleMapsHref({
    latitude,
    longitude,
    query: locationQuery,
  });

  if (!href || !hasGoogleMapsTarget({ latitude, longitude, query: locationQuery })) {
    return null;
  }

  const subtitle =
    description ||
  'Opens in the Google Maps app when available, otherwise in your browser.';

  if (variant === 'card') {
    return (
      <div className={className}>
        {heading && (
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{heading}</h3>
        )}
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="group block w-full rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-blue-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          aria-label={label}
        >
          <div className="flex flex-col items-center justify-center text-center px-4 py-10 min-h-[10rem]">
            <MapPinIcon className="w-12 h-12 mx-auto mb-3 text-blue-600 group-hover:text-blue-700" />
            <p className="font-medium text-gray-900 group-hover:text-blue-700">{label}</p>
            <p className="text-sm text-gray-500 mt-1 max-w-sm">{subtitle}</p>
            {locationQuery && (
              <p className="text-xs text-gray-400 mt-2 line-clamp-2 max-w-md">{locationQuery}</p>
            )}
          </div>
        </a>
      </div>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={
        className ||
        'text-blue-600 hover:text-blue-800 text-sm font-medium inline-flex items-center gap-1'
      }
      aria-label={typeof children === 'string' ? children : label}
    >
      {children || (
        <>
          <MapPinIcon className="w-4 h-4 flex-shrink-0" />
          <span>{label}</span>
          <ExternalLinkIcon className="w-3.5 h-3.5 opacity-70" />
        </>
      )}
    </a>
  );
};

export default GoogleMapsLocationLink;
